import _extends from '../../../node_modules/@babel/runtime/helpers/extends.js';
import _objectWithoutProperties from '../../../node_modules/@babel/runtime/helpers/objectWithoutProperties.js';
import _defineProperty from '../../../node_modules/@babel/runtime/helpers/defineProperty.js';
import React from 'react';
import 'prop-types';
import { jsx } from '@emotion/core';
import { useReactlyticsHandlers } from '@americanexpress/reactlytics';
import merge from 'deepmerge';
import { useFieldControlContext } from '../FieldControl/FieldControlContext.js';
import noop from '../../utils/noop.js';
import getPreferredValue from '../../utils/getPreferredValue.js';
import forms from '../../styles/forms.js';
import IconWarning from '../../svg/IconWarning.js';
import IconSuccess from '../../svg/IconSuccess.js';
import { dlsColorWarning } from '../../styles/colorsDls/dlsColorWarning.js';
import { dlsColorSuccess } from '../../styles/colorsDls/dlsColorSuccess.js';
import { positionRelative } from '../../styles/layout/positionRelative.js';
import { useThemeContext } from '../ThemeProvider/ThemeContext.js';

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

var getIconComponent = function getIconComponent(inputStatus, iconStyleObj) {
  var iconComponent;

  if (inputStatus === 'error') {
    iconComponent = jsx(IconWarning, {
      filled: true,
      size: "md",
      theme: _objectSpread(_objectSpread({}, dlsColorWarning), iconStyleObj)
    });
  }

  if (inputStatus === 'success') {
    iconComponent = jsx(IconSuccess, {
      filled: false,
      size: "md",
      theme: _objectSpread(_objectSpread({}, dlsColorSuccess), iconStyleObj)
    });
  }

  return iconComponent;
};

var Input = /*#__PURE__*/React.forwardRef(function (props, ref) {
  var _useReactlyticsHandle = useReactlyticsHandlers(props, ['onBlur', 'onFocus', 'onChange']),
      className = _useReactlyticsHandle.className,
      id = _useReactlyticsHandle.id,
      name = _useReactlyticsHandle.name,
      type = _useReactlyticsHandle.type,
      status = _useReactlyticsHandle.status,
      showStateIcon = _useReactlyticsHandle.showStateIcon,
      onChange = _useReactlyticsHandle.onChange,
      onBlur = _useReactlyticsHandle.onBlur,
      area = _useReactlyticsHandle.area,
      required = _useReactlyticsHandle.required,
      describedBy = _useReactlyticsHandle.describedBy,
      theme = _useReactlyticsHandle.theme,
      children = _useReactlyticsHandle.children,
      otherProps = _objectWithoutProperties(_useReactlyticsHandle, ["className", "id", "name", "type", "status", "showStateIcon", "onChange", "onBlur", "area", "required", "describedBy", "theme", "children"]);

  var themeTokens = useThemeContext();
  var fieldControlContext = useFieldControlContext();

  var _ref = fieldControlContext || {},
      handleFieldTouch = _ref.handleFieldTouch,
      contextStatus = _ref.contextStatus;
  /**
   * Calls FieldControl handler if provided by context and then onBlur from props
   *
   * @param {Object} event - The Synthetic React onBlur event* @param event
   */


  var handleInputBlur = function handleInputBlur(event) {
    if (handleFieldTouch) handleFieldTouch(event.target.value, required);
    onBlur(event);
  }; // Use context valid state if present


  var inputStatus = getPreferredValue(contextStatus, status);
  var inputStyleObject = merge(forms.formControl, themeTokens.primaryColor ? {
    '&:active': {
      borderColor: themeTokens.primaryColor
    },
    '&:focus': {
      borderColor: themeTokens.primaryColor
    }
  } : {});

  if (inputStatus === 'error') {
    inputStyleObject = _objectSpread(_objectSpread(_objectSpread({}, inputStyleObject), forms.hasWarning__formControl), showStateIcon ? {
      paddingRight: '3.125rem'
    } : {}); // TODO: Remove once the focus state on dls has been fixed, only here for feature parity

    inputStyleObject = _objectSpread(_objectSpread({}, inputStyleObject), {}, {
      '&:focus': _objectSpread(_objectSpread({}, forms.formControl['&:focus']), {}, {
        borderColor: dlsColorWarning.color
      })
    });
  }

  if (inputStatus === 'success') {
    inputStyleObject = _objectSpread(_objectSpread({}, inputStyleObject), showStateIcon ? {
      paddingRight: '3.125rem'
    } : {});
  }

  var ComponentToRender = 'input';
  var typeToUse = type;

  if (area) {
    ComponentToRender = 'textarea';
    typeToUse = undefined;
  }

  var inputComponent = jsx(ComponentToRender, _extends({
    id: id,
    type: typeToUse,
    name: name || id,
    "aria-invalid": inputStatus === 'error',
    "aria-required": required,
    required: required,
    "aria-describedby": describedBy,
    css: inputStyleObject,
    onChange: onChange,
    onBlur: handleInputBlur
  }, otherProps, {
    ref: ref
  }));
  var iconStyleObj = {
    position: 'absolute',
    top: '0.6875rem',
    right: '1rem'
  };
  var iconComponent = getIconComponent(inputStatus, iconStyleObj);
  return jsx("div", {
    css: _objectSpread(_objectSpread({}, positionRelative), {}, {
      marginTop: '0.3125rem'
    }, theme),
    className: className
  }, inputComponent, showStateIcon ? iconComponent : children);
});
Input.displayName = 'Input';
Input.defaultProps = {
  className: '',
  name: '',
  type: 'text',
  value: undefined,
  status: 'default',
  showStateIcon: false,
  onChange: noop,
  onBlur: noop,
  area: false,
  required: false,
  describedBy: undefined,
  theme: undefined,
  children: null
};

export default Input;
